import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBoundary, NetworkStatusBanner } from '@/components/ErrorBoundary';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ThreePanelTaskPage from '@/pages/ThreePanelTaskPage';
import AllTasksListPage from '@/pages/AllTasksListPage';
import ExecutorTasksListPage from '@/pages/ExecutorTasksListPage';
import CreateTaskPage from '@/pages/CreateTaskPage';
import AdminDashboard from '@/pages/AdminDashboard';
import DesignersPage from '@/pages/DesignersPage';
import UserManagementPage from '@/pages/UserManagementPage';
import AuditLogPage from '@/pages/AuditLogPage';
import AdminLLMConfigPage from '@/pages/AdminLLMConfigPage';
import QuotationEnginePage from '@/pages/QuotationEnginePage';
import CADSheetPage from '@/pages/CADSheetPage';
import ManufacturingPage from '@/pages/ManufacturingPage';
import ProductDevAIPage from '@/pages/ProductDevAIPage';
import PortalImportPage from '@/pages/PortalImportPage';
import StoneRequirementsListPage from '@/pages/stones/StoneRequirementsListPage';
import CreateStoneRequirementPage from '@/pages/stones/CreateStoneRequirementPage';
import StoneRequirementDetailPage from '@/pages/stones/StoneRequirementDetailPage';
import SupplierQuoteFormPage from '@/pages/stones/SupplierQuoteFormPage';
import StoneSuppliersAdminPage from '@/pages/stones/StoneSuppliersAdminPage';
import StoneDashboardPage from '@/pages/stones/StoneDashboardPage';
import StoneWorkspacePage from '@/pages/stones/StoneWorkspacePage';
import StoneNotificationsPage from '@/pages/stones/StoneNotificationsPage';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { isStonesOnlyHost } from '@/utils/host';

const ProtectedRoute = ({ children, adminOnly = false, allowedRoles = null }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-ivory">
        <div className="ks-loader" />
        <div className="eyebrow text-muted-foreground">LOADING</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // On the stones-only subdomain (diamondhq.*), every non-/stones/* route is
  // redirected to /stones so the rest of the app is invisible there.
  if (isStonesOnlyHost() && !location.pathname.startsWith('/stones') && location.pathname !== '/login') {
    return <Navigate to="/stones" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to={user.role === 'stone_supplier' ? '/stones' : '/tasks'} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === 'stone_supplier' ? '/stones' : '/tasks'} replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  const getDefaultRoute = () => {
    if (!user) return '/login';
    if (isStonesOnlyHost()) return '/stones';
    if (user.role === 'stone_supplier') return '/stones';
    return '/tasks';
  };

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getDefaultRoute()} replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <ThreePanelTaskPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/all-tasks"
        element={
          <ProtectedRoute>
            <AllTasksListPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/executor-tasks"
        element={
          <ProtectedRoute allowedRoles={['admin', 'special_executor']}>
            <ExecutorTasksListPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/tasks/new"
        element={
          <ProtectedRoute>
            <CreateTaskPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/analytics"
        element={
          <ProtectedRoute allowedRoles={['admin', 'special_executor']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/users"
        element={
          <ProtectedRoute adminOnly>
            <UserManagementPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute adminOnly>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/llm-config"
        element={
          <ProtectedRoute adminOnly>
            <AdminLLMConfigPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/quotation"
        element={
          <ProtectedRoute>
            <QuotationEnginePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/cad-sheets"
        element={
          <ProtectedRoute>
            <CADSheetPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/manufacturing"
        element={
          <ProtectedRoute>
            <ManufacturingPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/pd-ai"
        element={
          <ProtectedRoute>
            <ProductDevAIPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/portal-import"
        element={
          <ProtectedRoute>
            <PortalImportPage />
          </ProtectedRoute>
        }
      />

      {/* ===== Stone Procurement (RFQ portal) ===== */}
      <Route
        path="/stones"
        element={
          <ProtectedRoute allowedRoles={['admin', 'stone_supplier']}>
            <StoneWorkspacePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stones/list"
        element={
          <ProtectedRoute allowedRoles={['admin', 'stone_supplier']}>
            <StoneRequirementsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stones/dashboard"
        element={
          <ProtectedRoute adminOnly>
            <StoneDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stones/notifications"
        element={
          <ProtectedRoute allowedRoles={['admin', 'stone_supplier']}>
            <StoneNotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stones/new"
        element={
          <ProtectedRoute adminOnly>
            <CreateStoneRequirementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stones/suppliers"
        element={
          <ProtectedRoute adminOnly>
            <StoneSuppliersAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stones/:id"
        element={
          <ProtectedRoute allowedRoles={['admin', 'stone_supplier']}>
            <StoneRequirementDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stones/:id/quote"
        element={
          <ProtectedRoute allowedRoles={['stone_supplier']}>
            <SupplierQuoteFormPage />
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <NetworkStatusBanner />
          <AppRoutes />
          <Toaster position="top-right" expand={false} richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
